import { game } from '../main';
import { makeImage } from '../utils/makeImage';
import { GridArray } from './GridArray';
import { BubbleFactory } from './BubbleFactory';
import { Bubble } from './Bubble';
import { BubbleFlying } from './BubbleFlying';
import { playSound } from '../handlers/playSound';
import { IGridConfig } from '../utils/getGameConfig';
import "babel-polyfill";
import type {FlyingBubble} from "js/Elements/BubbleFlyingBehaviour";
import {BubblesMatchBehaviours} from "js/Elements/BubblesMatchBehaviours";

const neighboursCoords = [
  [
    [-1, -1],
    [-1, 0],
    [-1, 1],
    [0, -1],
    [0, 1],
    [1, 0]
  ],
  [
    [0, -1],
    [-1, 0],
    [0, 1],
    [1, -1],
    [1, 1],
    [1, 0]
  ]
]

export class GameGrid extends Phaser.Group {
  public signals = {
    onGameOver: new Phaser.Signal(),
    onMoveDone: new Phaser.Signal(),
    onFxRemoveBubblesRequest: new Phaser.Signal(),
    onRemovedToPopOut: new Phaser.Signal(),
    onGameOverBubblesPopped: new Phaser.Signal(),
  };

  public bg: Phaser.Image;
  public usedRewarded: boolean = false;
  private gameOverTimer: number = 0;
  private bubbleFactory: BubbleFactory;
  public bubblesPopped: number = 0;
  public gridArray: GridArray;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private nonCacheGroup: Phaser.Group;
  private matchGroup: Phaser.Group;

  public gridFiledRect: any;

  private sizeW: number = 51;
  private sizeH: number = 50;

  private maxY: number = 400;
  private moveDelay: number = 0;

  private cellW: number = 51;
  private cellH: number = 50;

  private caching: boolean = true;
  private orderCacheAfterUpdate: boolean = false;
  private holds: any[] = [];
  private cachingCelling: boolean = true;
  private oldHeight: number = 30;

  private targetY: number;

  constructor(config: IGridConfig) {
    super(game);

    this.usedRewarded = false;

    this.position.set(config.x, config.y);
    if (config.mobile) {
      this.bg = makeImage(config.x - 1, config.y - 1, 'gamefield_p', 0);
    } else {
      this.bg = makeImage(config.x - 1, config.y - 3, 'gamefield_l', 0);
    }
    this.bg.moveDown();


    this.gridArray = new GridArray();
    this.nonCacheGroup = game.add.group();
    this.bubbleFactory = new BubbleFactory(this);

    this.sizeW = config.sizeW;
    this.sizeH = config.sizeH;

    this.gridFiledRect = new Phaser.Rectangle(this.x - 15, this.y, this.sizeW * this.cellW + (this.cellW * 0.5) + 30, (this.sizeH + 1) * this.cellH);
    this.gridFiledRect.offset = 2;
    this.gridFiledRect.radius = 10;

    this.generateLevel(config.fillTo);

    this.oldHeight = this.height;

    this.recacheBitmap();
  }

  public update() {
    this.nonCacheGroup.y = this.y;
    this.nonCacheGroup.x = this.x;

    if (game.config.gameOver && this.children.length > 0 && this.gameOverTimer++ % 3 == 0) {
      const bubble = game.rnd.pick(this.getAllBubbles());

      this.destroyBubble(bubble);
      bubble.pointsAfterBurst = 0;
      this.signals.onFxRemoveBubblesRequest.dispatch([bubble]);
      this.orderCacheAfterUpdate = true;

    } else if (game.config.gameOver && this.children.length === 0) {
      this.signals.onGameOverBubblesPopped.dispatch();
    }
  }

  public postUpdate(): void {
    if (this.fixedToCamera) {
      this.x = this.game.camera.view.x + this.cameraOffset.x;
      this.y = this.game.camera.view.y + this.cameraOffset.y;
    }

    var i = this.children.length;

    while (i--) {
      // @ts-ignore
      this.children[i].postUpdate();
    }

    if (this.orderCacheAfterUpdate) {
      this.recacheBitmap();
    }
  }

  private generateLevel(fillTo) {
    for (var row = 0; row < fillTo; row++) {
      for (var coll = 0, len = this.sizeW; coll < len; coll++) {
        var bubble = this.makeBubble(coll, row, this.getBubbleType());
        if (row == 0) this.holds.push([coll, row]);
      }
    }
  }

  private getBubbleType(): number {
    return this.game.rnd.between(0, 5);
  }

  public getAllColorsOnBoard(): number[] {
    return this.getAllBubbles()
      .reduce((acc, current) => {
        if (!acc.includes(current.bubbleType)) {
            acc.push(current.bubbleType);
        }
        return acc;
      }, [] as number[]);
  }

  private recacheBitmap(): void {
    if (!this.caching) return;
    this.orderCacheAfterUpdate = false;
    // @ts-ignore
    this.updateCache();
  }

  private makeBubble(x: number, y: number, type: number): Bubble {
    this.orderCacheAfterUpdate = true;
    return this.bubbleFactory.makeBubble(x, y, type);
  }

  private makeBubbleFromFlyingBubble(flyingBubble: FlyingBubble): Bubble {
    this.prepareBubbleToBePut(flyingBubble);

    var newBubble = this.makeBubble(flyingBubble.cell.x, flyingBubble.cell.y, flyingBubble.bubbleType);

    var xx = flyingBubble.x;
    var yy = flyingBubble.y;
    newBubble.x = flyingBubble.x - this.x;
    newBubble.y = flyingBubble.y;
    newBubble.velX = flyingBubble.vel.x;
    newBubble.velY = flyingBubble.vel.y;

    return newBubble;
  }

  private isSpaceFree(cx: number, cy: number): boolean {
    return this.getBubble(cx, cy) ? false : true;
  }

  public cellToInsidePx(x: number, y: number): [number, number] {
    if (y % 2 == 0) {
      return [Math.floor(x * this.cellW + (this.cellW * 0.5) - this.offsetX), Math.floor(y * this.cellH + (this.cellW * 0.5) - this.offsetY)];
    } else {
      return [Math.floor(x * this.cellW + this.cellW - this.offsetX), Math.floor(y * this.cellH + (this.cellW * 0.5) - this.offsetY)];
    }
  }

  private cellToOutsidePx(x: number, y: number): [number, number] {
    const pos = this.cellToInsidePx(x, y);
    pos[1] += this.y;
    return pos;
  }

  public outsidePxToCell(x: number, y: number): [number, number] {
    return this.insidePxToCell(x - this.x, y - this.y);
  }

  private insidePxToCell(x: number, y: number): [number, number] {
    let clean: boolean;

    y += 6;

    clean = y < 0 ? y % this.cellH > -34 : y % this.cellH > 17;

    let xx, yy, modX, modY;

    yy = Math.floor(y / this.cellH);

    if (!clean) {

      modX = yy % 2 == 0 ? x % this.cellW : (x - (this.cellW * 0.5)) % this.cellW;
      modX = modX < 0 ? this.cellW + modX : modX;
      modY = y > 0 ? y % this.cellH : this.cellH - Math.abs(y % this.cellH);

      if (modX + modY < 23 || modX + modY > 52) {
        yy--;
      }

      if (yy % 2 == 0) {
        xx = Math.floor(x / this.cellW);
      } else {
        xx = Math.floor((x - (this.cellW * 0.5)) / this.cellW)
      }

    } else {
      if (yy % 2 == 0) {
        xx = Math.floor(x / this.cellW);
      } else {
        xx = Math.floor((x - (this.cellW * 0.5)) / this.cellW)
      }
    }

    return [xx, yy];
  }

  private prepareBubbleToBePut(bubble: FlyingBubble): void {
    bubble.y -= this.y;
  }

  public checkCollisionAgainst(bubble: FlyingBubble, against: Bubble[], allColl?: boolean) {
    var circle = this.prepareBubbleCollCircleToCollCheck(bubble);

    var coll: Bubble[] = [];

    if (this.cachingCelling && bubble.cell.y == 0) return [bubble.cell.x, bubble.cell.y];

    for (var i = 0; i < against.length; i++) {
      if (against[i] && Phaser.Circle.intersects(circle, against[i].collCircle)) {
        if (allColl) {
          coll.push(against[i]);
        } else {
          return [against[i].cellX, against[i].cellY];
        }

      }
    }

    return coll;
  }

  private prepareBubbleCollCircleToCollCheck(bubble: FlyingBubble) {
    var circle = bubble.collCircle
    circle.x = bubble.x - this.x + bubble.vel.x;
    circle.y = bubble.y - this.y + bubble.vel.y;
    return bubble.collCircle;
  }

  public getBubble(cellX: number, cellY: number): Bubble | null {
    return this.gridArray.get(cellX, cellY);
  }

  private clearCheck() {
    this.getAllBubbles().forEach((child) => child.clearCheck());
  }

  public getNeighbours(cellX: number, cellY: number): Bubble[] {
    let result: Bubble[] = [];

    neighboursCoords[Math.abs(cellY % 2)].forEach((coords) => {
      let bubble = this.getBubble(cellX + coords[0], cellY + coords[1]);
      if (bubble) result.push(bubble);
    });

    return result;
  }

  public putBubble(flyingBubble): void {
    const newBubble = this.makeBubbleFromFlyingBubble(flyingBubble);

    let matching;

    if (this.getBubble(newBubble.cellX, newBubble.cellY)) {
      matching = BubblesMatchBehaviours.getMatchingBubbles(this, { x: newBubble.cellX, y: newBubble.cellY }, newBubble.bubbleType).map((cell) => {
        return this.getBubble(cell.x, cell.y);
      })

      if (matching.length > 0) {
        matching.forEach((bubble) => {
          this.destroyBubble(bubble);
        });
      } else {
        this.bounceBubbles(newBubble);
      }
    }

    var popOuts = this.checkAndProcessHold();
    popOuts.forEach(this.destroyBubble);
    matching.forEach((e, index) => e.pointsAfterBurst = (Math.floor(index / 3) + 1) * 10);
    popOuts.forEach((e, index) => e.pointsAfterBurst = (Math.floor(index / 3) + 1) * 100);

    const toDestroy = matching.concat(popOuts);
    this.bubblesPopped += toDestroy.length;

    if (toDestroy.length == 0) {
      playSound('bubble_hits_bubble');
    }

    this.signals.onFxRemoveBubblesRequest.dispatch(toDestroy);
    this.signals.onMoveDone.dispatch(toDestroy.length > 0);

    this.orderCacheAfterUpdate = true;

    if (this.getLowestRowNumber() >= this.sizeH) {
      this.signals.onGameOver.dispatch(false);
    }

    if (this.getAllBubbles().length == 0) {
      this.signals.onGameOver.dispatch(true);
    }
  }

  private async bounceBubbles(bubble) {
    this.nonCacheGroup.add(bubble);
    bubble.signals.onBounceFinished.addOnce(() => {
      this.add(bubble);
      this.orderCacheAfterUpdate = true;
    });
    bubble.startBounce(bubble.velX * 0.5, bubble.velY * 0.5);
  }

  private checkAndProcessHold() {
    this.checkHold();
    return this.getAllNotChecked();
  }

  private checkHold(): void {
    this.clearCheck();
    this.holds.forEach((child) => {
      this.holdCheckFrom(child[0], child[1]);
    }, this);
  }

  private holdCheckFrom(cellX: number, cellY: number) {
    if (!this.getBubble(cellX, cellY)) return;
    if (this.getBubble(cellX, cellY).checked) return;

    var toCheck = [[cellX, cellY]];
    var toCheckIndex = 0;
    this.getBubble(cellX, cellY).checked = true;

    while (toCheckIndex < toCheck.length) {
      const bubble = this.getBubble(toCheck[toCheckIndex][0], toCheck[toCheckIndex][1]);
      toCheckIndex++;

      const neighbours = this.getNeighbours(bubble.cellX, bubble.cellY);

      for (var i = 0; i < 6; i++) {
        if (neighbours[i] && !neighbours[i].checked) {
          neighbours[i].checked = true;
          toCheck.push([neighbours[i].cellX, neighbours[i].cellY]);
        }
      }
    }
  }

  private getAllNotChecked() {
    return this.getAllBubbles().filter((bubble) => !bubble.checked);
  }

  public makeRefill(): void {
    const rowsToRefill = 1;
    // var rowsToRefill = Math.min(7 - this.getAllColorsOnBoard().length, 1);

    this.moveAllBubblesDown(rowsToRefill);
    this.fillTopRows(rowsToRefill);

    var popOuts = this.checkAndProcessHold();

    popOuts.forEach(this.destroyBubble);
    popOuts.forEach((e, index) => e.pointsAfterBurst = 100);
    this.signals.onFxRemoveBubblesRequest.dispatch(popOuts);
    this.orderCacheAfterUpdate = true;

    if (this.getLowestRowNumber() >= this.sizeH) {
      this.signals.onGameOver.dispatch(false);
    }
  }

  private moveAllBubblesDown(amount: number): void {
    this.gridArray.clear();

    this.getAllBubbles().forEach((bubble) => {
      bubble.cellY += amount;
      var insidePx = this.cellToInsidePx(bubble.cellX, bubble.cellY);
      bubble.x = insidePx[0];
      bubble.y = insidePx[1];
      bubble.orgX = insidePx[0];
      bubble.orgY = insidePx[1];
      bubble.collCircle.x = insidePx[0];
      bubble.collCircle.y = insidePx[1];

      this.gridArray.set(bubble.cellX, bubble.cellY, bubble);
    });
  }

  private fillTopRows(rowsToRefill): void {
    const colorsAvailable = this.getAllColorsOnBoard();
    for (let row = 0; row < rowsToRefill; row++) {
      for (let coll = 0, len = this.sizeW; coll < len; coll++) {
        this.makeBubble(coll, row, game.rnd.pick(colorsAvailable));
        if (row == 0) this.holds.push([coll, row]);
      }
    }
  }

  private removeLastRows(number: number) {
    const target = this.sizeH-3;
    this.getAllBubbles()
      .filter((bubble) => bubble.cellY >= target)
      .forEach(this.destroyBubble);
    this.orderCacheAfterUpdate = true;
  }

  private getLowestRowNumber(): number {
    return this.getAllBubbles()
      .reduce((acc, current) => Math.max(acc, current.cellY || 0), 0);
  }

  private getAllBubbles(): Bubble[] {
    return [...this.children, ...this.nonCacheGroup.children] as Bubble[];
  }

  private destroyBubble = (bubble) => {
    this.gridArray.set(bubble.cellX, bubble.cellY, null);
    bubble.kill();
  }
}
